import { useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import api from "./api";

function SeoManager() {
  const location = useLocation();
  const [seo, setSeo] = useState(null);

  const DEFAULT_IMAGE = window.location.origin + "/assets/images/og_image.jpg";

  /* ================================
     DEFAULT SEO (Fallback)
  ================================= */
  const setDefaultMetaTags = useCallback(() => {
    const canonicalUrl = window.location.origin + window.location.pathname;
    document.title = "Chettinad Hospital & Research Institute";

    updateMetaTag(
      "description",
      "Chettinad Hospital & Research Institute - Premier medical education and healthcare institution"
    );

    updateMetaTag(
      "keywords",
      "medical college, hospital, research, healthcare, India"
    );

    updatePropertyMeta("og:title", "Chettinad Hospital & Research Institute");
    updatePropertyMeta(
      "og:description",
      "Premier medical education and healthcare institution"
    );
    updatePropertyMeta("og:image", DEFAULT_IMAGE);
    updatePropertyMeta("og:type", "website");
    updatePropertyMeta("og:url", window.location.href);

    updateMetaTag("twitter:card", "summary_large_image");
    updateMetaTag("twitter:title", "Chettinad Hospital & Research Institute");
    updateMetaTag("twitter:description", "Premier medical education and healthcare institution");
    updateMetaTag("twitter:image", DEFAULT_IMAGE);
    updateCanonicalUrl(canonicalUrl);
  }, []);

  /* ================================
     FETCH SEO ON ROUTE CHANGE
  ================================= */
  useEffect(() => {
    const fetchSeoData = async () => {
      let path = location.pathname.replace(/^\/+|\/+$/g, "");
      if (!path) path = "home";

      try {
        const response = await api.get(`/api/seo/${path}`);

        if (!response.data?.success) {
          setDefaultMetaTags();
          return;
        }

        const seoData = response.data.data;
        setSeo(seoData);

        const canonicalUrl = seoData.canonical_url || window.location.origin + window.location.pathname;

        /* ========== BASIC META ========== */
        document.title =
          seoData.meta_title || "Chettinad Hospital & Research Institute";

        updateMetaTag("description", seoData.meta_description);
        updateMetaTag("keywords", seoData.meta_keywords);

        /* ========== IMAGE HANDLING ========== */
        let image = seoData.schema_data?.image || "/assets/images/og_image.jpg";

        if (image && !image.startsWith("http")) {
          image = process.env.REACT_APP_API_URL + "/public" + image;
        }

        /* ========== OPEN GRAPH ========== */
        updatePropertyMeta("og:title", seoData.meta_title);
        updatePropertyMeta("og:description", seoData.meta_description);
        updatePropertyMeta("og:url", window.location.href);
        updatePropertyMeta("og:type", "website");

        if (image) {
          updatePropertyMeta("og:image", image);
        }

        /* ========== TWITTER ========== */
        updateMetaTag("twitter:card", "summary_large_image");
        updateMetaTag("twitter:title", seoData.meta_title);
        updateMetaTag("twitter:description", seoData.meta_description);

        if (image) {
          updateMetaTag("twitter:image", image);
        }

        /* ========== SCHEMA JSON ========== */
        updateSchemaData(seoData.schema_data);
        updateCanonicalUrl(canonicalUrl);
      } catch (error) {
        console.error("SEO API Error:", error);
        setDefaultMetaTags();
      }
    };

    fetchSeoData();
  }, [location.pathname, setDefaultMetaTags]);

  return null;
}

/* ================================
   HELPERS
================================ */

const updateMetaTag = (name, content) => {
  if (!content) return;

  let meta = document.querySelector(`meta[name="${name}"]`);
  if (!meta) {
    meta = document.createElement("meta");
    meta.setAttribute("name", name);
    document.head.appendChild(meta);
  }
  meta.setAttribute("content", content);
};

const updatePropertyMeta = (property, content) => {
  if (!content) return;

  let meta = document.querySelector(`meta[property="${property}"]`);
  if (!meta) {
    meta = document.createElement("meta");
    meta.setAttribute("property", property);
    document.head.appendChild(meta);
  }
  meta.setAttribute("content", content);
};

const updateSchemaData = (schemaData) => {
  if (!schemaData) return;

  let script = document.querySelector('script[type="application/ld+json"]');
  if (!script) {
    script = document.createElement("script");
    script.type = "application/ld+json";
    document.head.appendChild(script);
  }
  script.text = JSON.stringify(schemaData);
};

const updateCanonicalUrl = (url) => {
  if (!url) return;

  let link = document.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }
  link.setAttribute("href", url);
};

export default SeoManager;